/* eslint-disable react-hooks/exhaustive-deps */
import PropTypes from 'prop-types';
import { useState } from 'react';
import {
    Box,
    Typography,
    Avatar,
    TextField,
    Button,
    Stack,
    Chip,
    IconButton,
    Skeleton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Tooltip,
    ClickAwayListener,
} from '@mui/material';
import Iconify from 'src/components/Iconify';
import { dispatch } from 'src/redux/store';
import { addComment, updateComment, deleteComment } from 'src/redux/slices/taskSlice';
import { useAuth } from 'src/hooks';
import { userKeyOf } from 'src/utils/authUser';
import { parseApiDate } from 'src/utils/taskDateUtils';

// -----------------------------------------------

const LEGACY_NOTE_LABELS = {
    FOLLOW_UP: 'Follow-up',
    UNABLE_TO_CONTACT: 'Unable to contact',
    CALLBACK: 'Callback',
    SPOKE_NOT_BOOKED: 'Spoke — not booked',
    NO_ANSWER: 'No answer',
    LEFT_VOICEMAIL: 'Left voicemail',
    BOOKED: 'Booked',
    ATTENDED: 'Attended',
    FAILED_TO_ATTEND: 'Failed to attend',
    ARCHIVED: 'Archived',
};

function humaniseNoteCode(code) {
    if (!code) return '';
    return LEGACY_NOTE_LABELS[code] || code.replace(/_/g, ' ').toLowerCase().replace(/^./, (c) => c.toUpperCase());
}

function formatCommentTime(dateStr) {
    if (!dateStr) return '';
    const d = parseApiDate(dateStr)?.toDate() || new Date();
    const now = new Date();
    const diffMs = now - d;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function getInitials(user) {
    if (!user) return '?';
    const first = user.first_name ? user.first_name.charAt(0).toUpperCase() : '';
    const last = user.last_name ? user.last_name.charAt(0).toUpperCase() : '';
    return first + last || (user.email ? user.email.charAt(0).toUpperCase() : '?');
}

function getDisplayName(user) {
    if (!user) return 'Unknown';
    return `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || 'Unknown';
}

// -----------------------------------------------

function CommentItem({ comment, currentUserId, practiceId, taskId, onRefresh }) {
    const [editing, setEditing] = useState(false);
    const [editText, setEditText] = useState(comment.body || comment.text || '');
    const [saving, setSaving] = useState(false);
    const [hovering, setHovering] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const authorId = comment.author?.id || comment.user?.id;
    // Lead-sourced rows (LeadNote) are rendered read-only: editing or
    // deleting them here would mutate the lead via the wrong endpoint
    // (this view dispatches against ``tasks/<id>/comments/`` which
    // doesn't know about LeadNote rows). The author still shows so
    // operators see who logged "UNABLE_TO_CONTACT" against the lead.
    const isLeadSourced = comment?._source === 'lead';
    const isOwn = (
        !isLeadSourced
        && currentUserId
        && authorId
        && String(authorId) === String(currentUserId)
    );
    const author = comment.author || comment.user;
    const bodyText = comment.body || comment.text || '';
    const createdAt = comment.created_at || comment.timestamp;

    const handleSaveEdit = () => {
        const trimmed = editText.trim();
        if (!trimmed) return;
        setSaving(true);
        dispatch(
            updateComment(practiceId, taskId, comment.id, { body: trimmed }, ({ success }) => {
                if (success) {
                    setEditing(false);
                    if (onRefresh) onRefresh();
                }
                setSaving(false);
            })
        );
    };

    const handleDelete = () => {
        setDeleting(true);
        dispatch(
            deleteComment(practiceId, taskId, comment.id, ({ success }) => {
                if (success) {
                    setConfirmDelete(false);
                    if (onRefresh) onRefresh();
                }
                setDeleting(false);
            })
        );
    };

    return (
        <Box
            className="task-slideout-comment-row"
            data-testid="task-slideout-comment-row"
            onMouseEnter={() => setHovering(true)}
            onMouseLeave={() => setHovering(false)}
            sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
                py: 1,
                borderRadius: 1,
                position: 'relative',
                '&:hover': { bgcolor: 'action.hover' },
            }}
        >
            {/* Avatar — smaller (24px) for tighter row density */}
            <Tooltip title={isLeadSourced ? 'Lead-scoped note (read-only here)' : ''} disableHoverListener={!isLeadSourced}>
                <Avatar
                    alt={getDisplayName(author)}
                    src={author?.profile_picture || undefined}
                    sx={{ width: 24, height: 24, fontSize: '0.65rem', bgcolor: isLeadSourced ? 'grey.400' : 'primary.main', flexShrink: 0, alignSelf: 'flex-start', mt: '2px' }}
                >
                    {getInitials(author)}
                </Avatar>
            </Tooltip>

            {/* Body */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
                {/* Header row: author + chip on the left, date right-aligned */}
                <Box className="task-slideout-comment-author" data-testid="task-slideout-comment-author" sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '13px', color: 'text.primary' }}>
                        {getDisplayName(author)}
                    </Typography>
                    {isLeadSourced && comment._legacy_note_code && (
                        <Chip
                            label={humaniseNoteCode(comment._legacy_note_code)}
                            size="small"
                            sx={{
                                height: 18,
                                fontSize: '10px',
                                fontWeight: 500,
                                bgcolor: 'rgba(0, 0, 0, 0.05)',
                                color: 'text.secondary',
                                borderRadius: '5px',
                                '& .MuiChip-label': { px: '6px' },
                            }}
                        />
                    )}
                    {comment.is_edited && (
                        <Typography sx={{ color: 'text.disabled', fontSize: '11px', fontStyle: 'italic' }}>
                            (edited)
                        </Typography>
                    )}
                    <Typography sx={{ ml: 'auto', color: 'text.disabled', fontSize: '11px', whiteSpace: 'nowrap', flexShrink: 0 }}>
                        {formatCommentTime(createdAt)}
                    </Typography>
                </Box>

                {editing ? (
                    <Box>
                        <TextField
                            fullWidth
                            multiline
                            size="small"
                            value={editText}
                            onChange={(e) => setEditText(e.target.value)}
                            autoFocus
                            sx={{ mb: 1, '& .MuiOutlinedInput-root': { fontSize: '0.875rem' } }}
                        />
                        <Stack direction="row" spacing={1}>
                            <Button
                                size="small"
                                variant="contained"
                                disabled={saving || !editText.trim()}
                                onClick={handleSaveEdit}
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    fontSize: '0.8rem',
                                    bgcolor: '#00AB55',
                                    '&:hover': { bgcolor: '#007B55' },
                                }}
                            >
                                {saving ? 'Saving…' : 'Save'}
                            </Button>
                            <Button
                                size="small"
                                variant="text"
                                onClick={() => {
                                    setEditing(false);
                                    setEditText(bodyText);
                                }}
                                sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8rem', color: 'text.secondary' }}
                            >
                                Cancel
                            </Button>
                        </Stack>
                    </Box>
                ) : bodyText ? (
                    <Typography className="task-slideout-comment-body" data-testid="task-slideout-comment-body" variant="body2" sx={{ color: 'text.primary', fontSize: '13px', lineHeight: 1.4, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {bodyText}
                    </Typography>
                ) : null}
            </Box>

            {/* Edit / Delete actions on hover (own comments only) */}
            {isOwn && !editing && hovering && (
                <Stack
                    direction="row"
                    spacing={0.25}
                    sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        bgcolor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        p: '2px',
                    }}
                >
                    <Tooltip title="Edit">
                        <IconButton
                            size="small"
                            onClick={() => setEditing(true)}
                            sx={{ width: 26, height: 26, color: 'text.secondary', '&:hover': { color: '#078DEE' } }}
                        >
                            <Iconify icon="eva:edit-2-fill" width={14} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                        <IconButton
                            size="small"
                            onClick={() => setConfirmDelete(true)}
                            sx={{ width: 26, height: 26, color: 'text.secondary', '&:hover': { color: '#FF4842' } }}
                        >
                            <Iconify icon="eva:trash-2-fill" width={14} />
                        </IconButton>
                    </Tooltip>
                </Stack>
            )}

            {/* Delete confirmation */}
            <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)} maxWidth="xs">
                <DialogTitle>Delete Comment</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this comment? This cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDelete(false)} sx={{ textTransform: 'none' }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDelete}
                        disabled={deleting}
                        color="error"
                        variant="contained"
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                        {deleting ? 'Deleting…' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

CommentItem.propTypes = {
    comment: PropTypes.object.isRequired,
    currentUserId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    taskId: PropTypes.string.isRequired,
    onRefresh: PropTypes.func,
};

CommentItem.defaultProps = {
    currentUserId: null,
    onRefresh: null,
};

// -----------------------------------------------

/**
 * Normalise a LeadNote row (V1-era lead-scoped activity) into the same
 * shape ``CommentItem`` already understands so we can render task
 * comments and lead notes side-by-side. LeadNote rows carry their
 * payload in ``note`` (e.g. ``"UNABLE_TO_CONTACT"``) plus an optional
 * free-text ``additional_notes`` field. Both ``id`` and ``created_at``
 * are prefixed/preserved so React keys stay unique across the two
 * sources and CommentItem doesn't try to dispatch ``updateComment`` /
 * ``deleteComment`` against a LeadNote (the comment is rendered
 * read-only for source='lead' — see the read-only guard below).
 */
function _normalizeLeadNoteAsComment(leadNote) {
    if (!leadNote || !leadNote.id) return null;
    const noteCode = leadNote.note || '';
    const additional = (leadNote.additional_notes || '').trim();
    // Body is just the operator's free-text addendum. The note-type enum
    // surfaces separately as a humanised chip in the rendered comment row.
    const body = additional;
    return {
        id: `ln_${leadNote.id}`,
        body,
        author: leadNote.created_by || null,
        created_at: leadNote.created_at || leadNote.updated_at,
        // Tag the source so the renderer can disable edit/delete and
        // show a small "Lead activity" chip — these rows belong to the
        // lead, not the task, and editing here would mutate every task
        // for that lead.
        _source: 'lead',
        _legacy_note_code: noteCode,
    };
}

export default function TaskDetailComments({
    comments,
    practiceId,
    taskId,
    isLoading,
    onRefresh,
    // V1→V2 parity (2026-05-13). When provided, lead-scoped LeadNote
    // rows for the task's lead are merged into the rendered list. This
    // restores the V1 sidebar behaviour where operators saw every
    // ``UNABLE_TO_CONTACT`` / ``FOLLOW_UP`` LeadNote logged against the
    // lead, regardless of which reminder they opened. ``leadId`` is
    // accepted for forward-compatibility (a future "Add lead note"
    // composer can target ``leads/<id>/notes/`` directly) but unused
    // by the current render path.
    leadNotes = [],
    // eslint-disable-next-line no-unused-vars
    leadId = null,
}) {
    const { user } = useAuth();
    const currentUserId = userKeyOf(user); // auth user has `pk`, not `id`

    const [newComment, setNewComment] = useState('');
    const [submitting, setSubmitting] = useState(false);
    // #219 — Minimal pen-icon composer: collapsed by default, expand on click
    const [composerOpen, setComposerOpen] = useState(false);

    const handleSubmit = () => {
        const text = newComment.trim();
        if (!text) return;
        setSubmitting(true);
        dispatch(
            addComment(practiceId, taskId, { body: text }, ({ success }) => {
                if (success) {
                    setNewComment('');
                    setComposerOpen(false);
                    if (onRefresh) onRefresh();
                }
                setSubmitting(false);
            })
        );
    };

    const handleCancelComposer = () => {
        setNewComment('');
        setComposerOpen(false);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            handleSubmit();
        } else if (e.key === 'Escape') {
            e.preventDefault();
            handleCancelComposer();
        }
    };

    if (isLoading) {
        return (
            <Box>
                {[1, 2].map((i) => (
                    <Stack key={i} direction="row" spacing={1.5} sx={{ mb: 2 }}>
                        <Skeleton variant="circular" width={34} height={34} sx={{ flexShrink: 0 }} />
                        <Box sx={{ flex: 1 }}>
                            <Skeleton variant="text" width="30%" height={18} sx={{ mb: 0.5 }} />
                            <Skeleton variant="text" width="80%" height={16} />
                            <Skeleton variant="text" width="60%" height={16} />
                        </Box>
                    </Stack>
                ))}
            </Box>
        );
    }

    // Build the unified comment stream:
    //   1. Task-scoped TaskComment rows (the V2-native source).
    //   2. Lead-scoped LeadNote rows for the task's lead (the V1 source
    //      the operator typed against — preserved for V1→V2 parity).
    // Sort by ``created_at`` descending so most recent activity is at
    // the top of the list, matching V1's ordering. Lead notes are
    // tagged ``_source='lead'`` so CommentItem can render them
    // read-only (editing here would mutate the lead, not the task).
    const taskComments = comments?.results || comments || [];
    const normalisedLeadNotes = (Array.isArray(leadNotes) ? leadNotes : [])
        .map(_normalizeLeadNoteAsComment)
        .filter(Boolean);
    const items = [...taskComments, ...normalisedLeadNotes].sort((a, b) => {
        const ta = a?.created_at ? new Date(a.created_at).getTime() : 0;
        const tb = b?.created_at ? new Date(b.created_at).getTime() : 0;
        return tb - ta;
    });

    return (
        <Box>
            {/* Comment list */}
            {items.length === 0 ? (
                <Typography variant="body2" sx={{ color: 'text.disabled', fontStyle: 'italic', py: 1, mb: 2 }}>
                    No comments yet. Be the first to add one.
                </Typography>
            ) : (
                <Box sx={{ mb: 2 }}>
                    {items.map((comment) => (
                        <CommentItem
                            key={comment.id}
                            comment={comment}
                            currentUserId={currentUserId}
                            practiceId={practiceId}
                            taskId={taskId}
                            onRefresh={onRefresh}
                        />
                    ))}
                </Box>
            )}

            {/* Add comment form — simple input, no avatar (#219 minimal pen-icon).
                Collapsed: greyed "Add a comment…" + pen icon.
                Expanded: standard-variant TextField + Send / Cancel. */}
            <Box className="task-slideout-comment-composer" data-testid="task-slideout-comment-composer" sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 2, mt: 2 }}>
                <Box sx={{ width: '100%' }}>
                    {!composerOpen ? (
                            <Box
                                role="button"
                                tabIndex={0}
                                aria-label="Add a comment"
                                data-testid="comment-composer-collapsed"
                                onClick={() => setComposerOpen(true)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        setComposerOpen(true);
                                    }
                                }}
                                sx={{
                                    display: 'flex', alignItems: 'center', gap: 0.75,
                                    py: 0.75, px: 0.5, cursor: 'pointer', borderRadius: '6px',
                                    color: 'text.disabled',
                                    '&:hover .comment-pen-icon': { opacity: 1, color: 'primary.main' },
                                    '&:hover': { bgcolor: 'action.hover', color: 'text.secondary' },
                                }}
                            >
                                <Typography sx={{ flex: 1, fontSize: '0.875rem', fontStyle: 'italic' }}>
                                    Add a comment…
                                </Typography>
                                <Iconify
                                    className="comment-pen-icon"
                                    icon="eva:edit-2-outline"
                                    width={14}
                                    sx={{ opacity: 0.45, color: 'text.disabled', transition: 'opacity 0.15s, color 0.15s', flexShrink: 0 }}
                                />
                            </Box>
                        ) : (
                            <ClickAwayListener
                                onClickAway={() => {
                                    if (!newComment.trim() && !submitting) setComposerOpen(false);
                                }}
                            >
                                <Box data-testid="comment-composer-expanded">
                                    <TextField
                                        className="task-slideout-comment-input"
                                        data-testid="task-slideout-comment-input"
                                        fullWidth
                                        multiline
                                        autoFocus
                                        minRows={2}
                                        maxRows={6}
                                        variant="standard"
                                        placeholder="Add a comment… (Ctrl+Enter to submit, Esc to cancel)"
                                        value={newComment}
                                        onChange={(e) => setNewComment(e.target.value)}
                                        onKeyDown={handleKeyDown}
                                        disabled={submitting}
                                        InputProps={{ disableUnderline: false }}
                                        sx={{
                                            '& .MuiInput-root': {
                                                fontSize: '0.875rem',
                                                '&:before': { borderBottomColor: 'divider' },
                                                '&:after': { borderBottomColor: 'primary.main' },
                                            },
                                        }}
                                    />
                                    <Stack direction="row" justifyContent="flex-end" sx={{ mt: 0.75 }} spacing={0.5}>
                                        <Tooltip title="Cancel (Esc)">
                                            <IconButton
                                                size="small"
                                                aria-label="Cancel comment"
                                                onClick={handleCancelComposer}
                                                disabled={submitting}
                                                sx={{ color: 'text.secondary' }}
                                            >
                                                <Iconify icon="eva:close-fill" width={16} />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Post comment (Ctrl+Enter)">
                                            <span>
                                                <IconButton
                                                    className="task-slideout-comment-submit"
                                                    data-testid="task-slideout-comment-submit"
                                                    size="small"
                                                    aria-label="Post comment"
                                                    onClick={handleSubmit}
                                                    disabled={submitting || !newComment.trim()}
                                                    sx={{
                                                        color: '#00AB55',
                                                        '&:hover': { bgcolor: 'rgba(0, 171, 85, 0.08)' },
                                                        '&.Mui-disabled': { color: 'text.disabled' },
                                                    }}
                                                >
                                                    <Iconify icon="eva:paper-plane-outline" width={16} />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </Stack>
                                </Box>
                            </ClickAwayListener>
                        )}
                </Box>
            </Box>
        </Box>
    );
}

TaskDetailComments.propTypes = {
    comments: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    taskId: PropTypes.string.isRequired,
    isLoading: PropTypes.bool,
    onRefresh: PropTypes.func,
};

TaskDetailComments.defaultProps = {
    comments: null,
    isLoading: false,
    onRefresh: null,
};
